import React, { useState } from "react";
import { Input, DatePicker } from "antd";
import dayjs from "dayjs";
import {
  useAllOrders,
  useExportOrders,
} from "../../../../../api/orders/orders";
import { notifyError } from "../../../../../utils/ToastifyProvider";
const { RangePicker } = DatePicker;

const GetAllOrder = () => {
  const role = localStorage.getItem("role");
  const userId = localStorage.getItem("userId");
  const [searchText, setSearchText] = useState("");
  const [status, setStatus] = useState("");
  const [currentPage, setCurrentPage] = useState(1);
  const [dateRange, setDateRange] = useState([null, null]);

  const exportOrders = useExportOrders();

  const {
    data: allOrders = { orders: [], totalPages: 1 },
    isLoading,
    isFetching,
  } = useAllOrders({
    currentPage,
    search: searchText,
    startDate: dateRange[0]
      ? dayjs(dateRange[0]).format("YYYY-MM-DD")
      : undefined,
    endDate: dateRange[1]
      ? dayjs(dateRange[1]).format("YYYY-MM-DD")
      : undefined,
    usersid: role !== "admin" ? userId : undefined,
  });

  const orders = allOrders.orders;
  const totalPages = allOrders.totalPages || 1;

  const handleExport = () => {
    if (!dateRange[0] || !dateRange[1]) {
      notifyError("Please select a date range before exporting.");
      return;
    }
    exportOrders.mutate({
      startDate: dayjs(dateRange[0]).format("YYYY-MM-DD"),
      endDate: dayjs(dateRange[1]).format("YYYY-MM-DD"),
      usersid: role !== "admin" ? userId : undefined, // Pass usersid here
    });
  };

  return (
    <div className="w-full mt-6">
      <div className="mb-4 flex justify-between items-center">
        <Input
          className="w-80"
          placeholder="Search by AWB, Order Number or Name"
          value={searchText}
          onChange={(e) => {
            setSearchText(e.target.value);
            setCurrentPage(1);
          }}
        />

        <div className="flex gap-2">
          <RangePicker
            className="w-80"
            format="YYYY-MM-DD"
            value={dateRange}
            onChange={(dates) => {
              setDateRange(dates || [null, null]);
              setCurrentPage(1);
            }}
            allowClear
          />

          {dateRange[0] && dateRange[1] && (
            <button
              onClick={handleExport}
              disabled={exportOrders.isLoading}
              className={`px-4 py-2 rounded text-white ${
                exportOrders.isLoading
                  ? "bg-primary cursor-not-allowed"
                  : "bg-primary hover:bg-primaryLight"
              }`}
            >
              {exportOrders.isLoading ? "Exporting..." : "Download"}
            </button>
          )}
        </div>
      </div>

      <div className="overflow-x-auto hide-scrollbar">
        <table className="min-w-[1200px] w-full table-auto border border-gray-200">
          <thead className="bg-gray-100 text-left text-sm font-semibold text-gray-700">
            <tr>
              <th className="p-3">Order Date</th>
              <th className="p-3">Order ID / Status</th>
              <th className="p-3">Payment</th>
              <th className="p-3">Products</th>
              <th className="p-3">Customer Name / Contact</th>
              <th className="p-3">Address</th>
            </tr>
          </thead>
          <tbody className="bg-white text-sm">
            {isLoading ? (
              <tr>
                <td colSpan={6} className="text-center py-6 text-gray-500">
                  Loading orders...
                </td>
              </tr>
            ) : orders.length > 0 ? (
              orders.map((order, index) => (
                <tr key={index} className="hover:bg-gray-50">
                  <td className="p-4">
                    {dayjs(order.createdAt).format("YYYY-MM-DD")}
                  </td>
                  <td className="p-4">
                    <div className="font-medium text-blue-600">
                      {order.OrderNumber}
                    </div>
                    <div
                      className={`text-xs font-semibold px-2 py-1 rounded inline-block mt-1 ${
                        order.shippingStatusText === "cancelled"
                          ? "bg-red-100 text-red-600"
                          : order.shippingStatusText === "in_transit"
                          ? "bg-blue-100 text-blue-600"
                          : order.shippingStatusText === "shipped"
                          ? "bg-green-100 text-green-600"
                          : "bg-yellow-100 text-yellow-600"
                      }`}
                    >
                      {order.shippingStatusText}
                    </div>
                  </td>
                  <td className="p-4">
                    <p>₹{order.InvoiceAmount}</p>
                    <span className="inline-block text-xs bg-green-100 text-green-700 px-2 py-1 rounded mt-1">
                      {order.PaymentType}
                    </span>
                  </td>
                  <td className="p-4">
                    {order.ProductDetails?.map((prod, i) => (
                      <p key={i} className="truncate">
                        {prod.name}
                      </p>
                    ))}
                  </td>
                  <td className="p-4">
                    <p>{order.CustomerName}</p>
                    <p>{order.BillingAddress?.Contact}</p>
                  </td>
                  <td className="p-4 text-sm">
                    <p>{order.BillingAddress?.City}</p>
                    <p>{order.BillingAddress?.State}</p>
                    <p>{order.BillingAddress?.Pincode}</p>
                  </td>
                </tr>
              ))
            ) : (
              <tr>
                <td colSpan={6} className="text-center py-6 text-gray-400">
                  No orders found.
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </div>

      <div className="flex justify-between items-center mt-6 text-sm text-gray-700">
        <p>
          Page <strong>{currentPage}</strong> of <strong>{totalPages}</strong>
        </p>

        <div className="space-x-2">
          <button
            onClick={() => setCurrentPage((prev) => Math.max(prev - 1, 1))}
            disabled={currentPage === 1 || isFetching}
            className={`px-4 py-2 rounded border transition ${
              currentPage === 1 || isFetching
                ? "bg-gray-100 text-gray-400 cursor-not-allowed"
                : "bg-white hover:bg-gray-50"
            }`}
          >
            Previous
          </button>
          <button
            onClick={() =>
              setCurrentPage((prev) => Math.min(prev + 1, totalPages))
            }
            disabled={currentPage === totalPages || isFetching}
            className={`px-4 py-2 rounded border transition ${
              currentPage === totalPages || isFetching
                ? "bg-gray-100 text-gray-400 cursor-not-allowed"
                : "bg-white hover:bg-gray-50"
            }`}
          >
            Next
          </button>
        </div>
      </div>
    </div>
  );
};

export default GetAllOrder;